import Button from '../../../reusable/Button/Button';
import CopyButton from '../../../reusable/CopyButton/CopyButton';
import DataTable from '../../../reusable/DataTable/DataTable';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import { useGetContractsQuery } from '../../../store/api/contractsApi';
import { useGetDashboardStatsQuery } from '../../../store/api/dashboardApi';
import { t } from '../../../i18n/i18n';
import './Contracts.scss';

/** The agreement is signed on DigiSigner's own page, so both actions just open it. */
const openDocument = (row) => window.open(row.document_link, '_blank', 'noopener,noreferrer');

export default function Contracts() {
  const { data, isLoading, isError } = useGetContractsQuery();
  const contracts = data?.data ?? [];
  const awaiting = contracts.filter((row) => !row.is_verified).length;

  /**
   * An empty list says why for this trader (N57): "No funded accounts yet"
   * was being told to traders with dozens. The Dashboard's own count says
   * which it is; until it's known, the message claims neither.
   */
  const { data: summary, isSuccess: hasSummary } = useGetDashboardStatsQuery();
  const fundedCount = Number(summary?.funded_balance?.account_count) || 0;
  const emptyMessage = isError
    ? t('Could not load your contracts. Please try again in a moment.')
    : !hasSummary
      ? t('No contracts yet.')
      : fundedCount > 0
        ? fundedCount === 1
          ? t("Your {{count}} qualified account has no contract issued yet — each appears here once it's sent for your signature.", { count: fundedCount })
          : t("Your {{count}} qualified accounts have no contract issued yet — each appears here once it's sent for your signature.", { count: fundedCount })
        : t('No qualified accounts yet — contracts appear here once you pass a challenge.');

  const columns = [
    {
      key: 'challenge_name',
      header: 'Challenge Name',
      width: 2.2,
      sortable: true,
    },
    {
      key: 'login_id',
      header: 'Login ID',
      width: 1.2,
      sortable: true,
      render: (row) => (
        <span className="contracts-login">
          {row.login_id}
          <CopyButton value={row.login_id} label={t('Login ID')} />
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 1.2,
      sortable: true,
      render: (row) => <StatusChip>{t(row.status)}</StatusChip>,
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 1.2,
      align: 'right',
      searchable: false,
      render: (row) => {
        if (!row.document_link) return null;
        return row.is_verified ? (
          <Button variant="toolbar" onClick={() => openDocument(row)}>
            {t('View contract')}
          </Button>
        ) : (
          <Button variant="pill" height={32} paddingInline={14} onClick={() => openDocument(row)}>
            {t('Review & sign')}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="contracts">
      <header className="pv-head">
        <div>
          <h1>{t('Contracts list')}</h1>
          <p>
            {t('One analyst agreement per qualified account. Sign each one to unlock performance fee requests on that login.')}
          </p>
        </div>
      </header>

      {awaiting > 0 && (
        <div className="contracts-banner" role="status">
          <span className="contracts-banner__count">{awaiting}</span>
          <p>
            {awaiting === 1
              ? t('One contract is waiting for your signature. Performance fees on that account unlock once signed.')
              : t('{{count}} contracts are waiting for your signature. Performance fees on those accounts unlock once signed.', { count: awaiting })}
          </p>
        </div>
      )}

      <DataTable
        className="contracts-table"
        title={t('Contracts list')}
        icon="table-accounts"
        columns={columns}
        rows={contracts}
        loading={isLoading}
        minWidth={620}
        searchPlaceholder={t('Search challenge or login')}
        emptyMessage={emptyMessage}
      />
    </div>
  );
}
